import { authClient } from "@/auth/auth-client";
import {
  LOCAL_ROLE_HEADER,
  getLocalRole,
  isLocalRoleSwitchingEnabled,
} from "@/auth/local-role";
import { ApiError } from "./errors";

// Empty locally: same-origin requests hit the Vite dev proxy → local backend.
const BASE_URL = import.meta.env.VITE_ADMIN_API_URL ?? "";

interface BackendErrorBody {
  error?: { message?: string; code?: string };
}

interface RequestOptions {
  /** Client-side timeout — for endpoints that proxy live partner APIs. */
  timeoutMs?: number;
  /** Cancel obsolete read requests when their query is no longer observed. */
  signal?: AbortSignal;
}

// The bearer token of the newest request, for `beacon`: it runs while the
// page unloads, when an awaited MSAL lookup would never get to send.
let lastToken: string | null = null;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const doFetch = async (forceRefresh: boolean): Promise<Response> => {
    options.signal?.throwIfAborted();
    const token = await authClient.getAccessToken({ forceRefresh });
    lastToken = token;
    options.signal?.throwIfAborted();
    const timeoutSignal =
      options.timeoutMs !== undefined
        ? AbortSignal.timeout(options.timeoutMs)
        : undefined;
    const signal =
      options.signal && timeoutSignal
        ? AbortSignal.any([options.signal, timeoutSignal])
        : (options.signal ?? timeoutSignal);
    try {
      return await fetch(`${BASE_URL}${path}`, {
        method,
        headers: {
          ...(body !== undefined && { "Content-Type": "application/json" }),
          ...(token && { Authorization: `Bearer ${token}` }),
          // Local dev only — the deployed backend ignores it entirely.
          ...(isLocalRoleSwitchingEnabled() && {
            [LOCAL_ROLE_HEADER]: getLocalRole(),
          }),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "TimeoutError") {
        throw new ApiError("Request timed out", 0, "TIMEOUT");
      }
      throw error;
    }
  };

  let response = await doFetch(false);
  if (response.status === 401 && authClient.mode === "msal") {
    // One silent-refresh retry; if the token is truly dead, go interactive
    // (the page navigates away and this promise never settles).
    response = await doFetch(true);
    if (response.status === 401) {
      await authClient.login();
    }
  }

  if (!response.ok) {
    const fallback = {
      message: `Request failed (${response.status})`,
      code: "UNKNOWN",
    };
    const parsed = await response.json().then(
      (json: BackendErrorBody) => json.error ?? fallback,
      () => fallback,
    );
    throw new ApiError(
      parsed.message ?? fallback.message,
      response.status,
      parsed.code ?? fallback.code,
    );
  }
  return (await response.json()) as T;
}

/**
 * POST to a Server-Sent-Events endpoint. Each `data:` event is parsed as
 * JSON and handed to `onEvent`; the final `{type:"done", ...}` event resolves
 * the promise (as T), a `{type:"error"}` event or transport failure rejects
 * with ApiError. Used by the AI chatbot's long-running turns.
 */
async function streamRequest<T>(
  path: string,
  body: unknown,
  onEvent: (event: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<T> {
  const doFetch = async (forceRefresh: boolean): Promise<Response> => {
    const token = await authClient.getAccessToken({ forceRefresh });
    return fetch(`${BASE_URL}${path}`, {
      method: "POST",
      // Aborting closes the connection, which the backend treats as "stop"
      // — it halts the turn and persists whatever already ran.
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(isLocalRoleSwitchingEnabled() && {
          [LOCAL_ROLE_HEADER]: getLocalRole(),
        }),
      },
      body: JSON.stringify(body),
    });
  };

  let response = await doFetch(false);
  if (response.status === 401 && authClient.mode === "msal") {
    response = await doFetch(true);
    if (response.status === 401) {
      await authClient.login();
    }
  }

  if (!response.ok || !response.body) {
    const fallback = {
      message: `Request failed (${response.status})`,
      code: "UNKNOWN",
    };
    const parsed = await response.json().then(
      (json: BackendErrorBody) => json.error ?? fallback,
      () => fallback,
    );
    throw new ApiError(
      parsed.message ?? fallback.message,
      response.status,
      parsed.code ?? fallback.code,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done: T | null = null;

  for (;;) {
    const { value, done: finished } = await reader.read();
    if (finished) break;
    buffer += decoder.decode(value, { stream: true });
    let separator;
    while ((separator = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      const dataLines = block
        .split("\n")
        .filter((line) => line.startsWith("data: "));
      if (dataLines.length === 0) continue; // keep-alive comments
      const event = JSON.parse(
        dataLines.map((line) => line.slice("data: ".length)).join(""),
      ) as Record<string, unknown>;
      if (event.type === "error") {
        throw new ApiError(
          typeof event.message === "string" ? event.message : "Request failed",
          0,
          "STREAM_ERROR",
        );
      }
      if (event.type === "done") {
        done = event as T;
      } else {
        onEvent(event);
      }
    }
  }

  if (done === null) {
    throw new ApiError(
      "The connection dropped before the answer arrived — reload to see whether the turn completed.",
      0,
      "STREAM_INCOMPLETE",
    );
  }
  return done;
}

/**
 * Fire-and-forget POST that outlives the page (`keepalive`), for flushing
 * work on `pagehide` — an ordinary fetch is cancelled by the navigation.
 * Sent synchronously with the newest request's token: no 401 retry, and
 * nobody is left to read the response — so it may never land (an expired
 * token, or a cross-origin preflight the browser drops on unload). Callers
 * that cannot lose the work must also keep it and resend it later.
 */
function beacon(path: string, body?: unknown): void {
  fetch(`${BASE_URL}${path}`, {
    method: "POST",
    keepalive: true,
    headers: {
      ...(body !== undefined && { "Content-Type": "application/json" }),
      ...(lastToken && { Authorization: `Bearer ${lastToken}` }),
      ...(isLocalRoleSwitchingEnabled() && {
        [LOCAL_ROLE_HEADER]: getLocalRole(),
      }),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }).catch(() => undefined);
}

/** The only fetch in the app (lint-enforced). All /admin calls go through here. */
export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>("GET", path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, body, options),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
  stream: streamRequest,
  beacon,
};
